// src/pages/pengamatan/buah-alami/BuahAlamiTableBody.jsx
import React from 'react';
import { TableBody, TableRow, TableCell, Box, IconButton, Chip, Checkbox } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import './css/BuahAlamiTableBody.css';
import PlotDetailTooltip from './PlotDetailTooltip';

// Import hook dan komponen dialog baru
import { usePlotEditDialog } from './hooks/usePlotEditDialog';
import PlotEditDialog from './PlotEditDialog';

const BuahAlamiTableBody = ({ 
  orders, 
  selectedItems,
  handleSelectItem,
  loadingOrders,
  currentTab,
  onStatusUpdate,
  userJabatan,
  userRoleNames
}) => {
  // Gunakan hook untuk dialog edit
  const {
    editDialogOpen,
    selectedOrder,
    plotStatuses,
    loading,
    handleOpenEditDialog,
    handleCloseEditDialog,
    handlePlotStatusChange,
    handleSavePlotStatuses
  } = usePlotEditDialog(onStatusUpdate);

  // FUNGSI BARU: Cek akses untuk ID Pengamatan
  const canViewPengamatanId = () => {
    return userJabatan === "Section Head" && userRoleNames.includes("Developer");
  };

  // FUNGSI BARU: Cek akses untuk edit (tombol EditIcon)
  const canAccessEdit = () => {
    // Hanya Admin atau Section Head dengan role Buah Alami atau Developer
    const isValidJabatan = userJabatan === "Admin" || userJabatan === "Section Head";
    const isValidRole = userRoleNames.includes("Buah Alami") || userRoleNames.includes("Developer");
    
    const canAccess = isValidJabatan && isValidRole;
    
    return canAccess;
  };

  // Fungsi untuk mendapatkan jumlah plot yang sesuai dengan tab aktif
  const getPlotCountForTab = (order) => {
    if (!order.allPlots) return 0;
    
    let count = 0;
    switch (currentTab) {
      case 'Pending':
        count = order.allPlots.filter(plot => plot.status_pengamatan === 'Pending').length;
        break;
      case 'Approved':
        count = order.allPlots.filter(plot => plot.status_pengamatan === 'Approved').length;
        break;
      case 'Processed':
        count = order.allPlots.filter(plot => plot.status_pengamatan === 'Processed').length;
        break;
      case 'Closing':
        count = order.allPlots.length;
        break;
      default:
        count = order.allPlots.length;
    }
    
    return count;
  };

  // FUNGSI YANG DIPERBAIKI: Handle view details - BUKA TAB BARU
  const handleViewDetails = (order, currentTab) => {
    // Buka tab baru dengan URL detail dan tambahkan parameter from
    const detailUrl = `/#/pengamatan/buah-alami/detail/${order.pengamatanKey}?from=${currentTab}`;
    window.open(detailUrl, '_blank');
};

  // FUNGSI BARU: Handle edit data - menggunakan hook
  const handleEditData = (order) => {
    handleOpenEditDialog(order, currentTab);
  };

  // FUNGSI BARU: Cek apakah tombol edit harus ditampilkan
  const shouldShowEditButton = () => {
    const hasAccess = canAccessEdit();
    const isCorrectTab = currentTab === 'Approved' || currentTab === 'Processed';
    
    return hasAccess && isCorrectTab;
  };

  // FUNGSI BARU: Cek apakah checkbox harus ditampilkan
  const shouldShowCheckbox = () => {
    const isCorrectTab = currentTab === 'Approved' || currentTab === 'Processed' || currentTab === 'Closing';
    
    // LOGIKA BARU: Di tab Processed dan Closing, SEMUA USER bisa melihat checkbox
    if (currentTab === 'Processed' || currentTab === 'Closing') {
      return true;
    }
    
    // Untuk tab Approved, hanya user dengan akses tertentu
    const hasAccess = canAccessEdit();
    return hasAccess && isCorrectTab;
  };

  // SATU RENDER UNTUK SEMUA TAB
  return (
    <>
      <TableBody>
        {orders.length > 0 ? (
          orders.map((order) => {
            const plotCount = getPlotCountForTab(order);
            const isSelected = selectedItems.includes(order.id);
            const showEditButton = shouldShowEditButton();
            const showCheckbox = shouldShowCheckbox();
            const canViewId = canViewPengamatanId();
            
            return (
              <TableRow 
                key={order.id}
                selected={isSelected}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  },
                }}
              >
                {/* Checkbox - LOGIKA BARU: Di tab Processed dan Closing semua user bisa lihat */}
                {showCheckbox && (
                  <TableCell className="buah-alami-table-body-cell checkbox">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectItem(order.id)}
                      color="primary"
                    />
                  </TableCell>
                )}
                
                {/* ID Pengamatan - DISEMBUNYIKAN JIKA TIDAK MEMILIKI AKSES */}
                {canViewId ? (
                  <TableCell className="buah-alami-table-body-cell">
                    <Box>
                      <div style={{ fontWeight: 'bold' }}>
                        {order.pengamatanKey || '-'}
                      </div>
                    </Box>
                  </TableCell>
                ) : (
                  // SEL TIDAK DITAMPILKAN SAMA SEKALI
                  null
                )}
                
                {/* Tanggal Pengamatan */}
                <TableCell className="buah-alami-table-body-cell">
                  {order.tanggal_pengamatan || '-'}
                </TableCell>
                
                {/* Lokasi */}
                <TableCell className="buah-alami-table-body-cell">
                  <Box>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      {order.lokasi || '-'}
                    </div>
                    <div style={{ fontSize: '0.8em', color: '#666' }}>
                      ID: {order.id_lokasi || '-'}
                    </div>
                  </Box>
                </TableCell>
                
                {/* FMP */}
                <TableCell className="buah-alami-table-body-cell">
                  {order.fmp || '-'}
                </TableCell>
                
                {/* Mandor */}
                <TableCell className="buah-alami-table-body-cell">
                  <Box>
                    {order.nama_mandor || '-'}
                  </Box>
                </TableCell>
                
                {/* Pengamat */}
                <TableCell className="buah-alami-table-body-cell">
                  <Box>
                    {order.nama_pengamat || '-'}
                  </Box>
                </TableCell>
                
                {/* Jumlah Plot - DITAMBAHKAN TOOLTIP */}
                <TableCell className="buah-alami-table-body-cell">
                  <Box sx={{ 
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%'
                  }}>
                    <PlotDetailTooltip order={order} currentTab={currentTab}>
                      <Chip 
                        label={`${plotCount} Plot`}
                        color={
                          currentTab === 'Pending' ? 'warning' :
                          currentTab === 'Approved' ? 'success' :
                          currentTab === 'Processed' ? 'info' : 'error'
                        }
                        size="small"
                        clickable
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8,
                            transform: 'scale(1.05)',
                            transition: 'all 0.2s ease-in-out'
                          }
                        }}
                      />
                    </PlotDetailTooltip>
                  </Box>
                </TableCell>
                
                {/* Aksi - DITAMBAHKAN TOMBOL EDIT */}
                <TableCell
                className="buah-alami-table-body-cell">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', }}>
                    {/* Tombol View (selalu tampil) */}
                    <IconButton 
                      color="primary" 
                      size="small"
                      title={`Lihat Detail ${plotCount} Plot`}
                      onClick={() => handleViewDetails(order, currentTab)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    
                    {/* Tombol Edit (hanya di tab Approved dan Processed DAN user dengan akses) */}
                    {showEditButton && (
                      <IconButton 
                        color="secondary" 
                        size="small"
                        title={`Edit Status ${plotCount} Plot`}
                        onClick={() => handleEditData(order)}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })
        ) : (
          <TableRow>
            <TableCell 
              colSpan={
                // PERHITUNGAN COLSPAN YANG LEBIH KOMPLEKS
                (() => {
                  let baseColspan = 7; // tanggal, lokasi, fmp, mandor, pengamat, jumlah_plot, aksi
                  
                  // Tambah 1 jika checkbox ditampilkan
                  if (shouldShowCheckbox()) baseColspan += 1;
                  
                  // Tambah 1 jika ID Pengamatan ditampilkan
                  if (canViewPengamatanId()) baseColspan += 1;
                  
                  return baseColspan;
                })()
              }
              align="center"
              className="no-data-cell"
            >
              {loadingOrders ? 'Memuat data...' : `Tidak ada data dengan status ${currentTab}.`}
            </TableCell>
          </TableRow>
        )}
      </TableBody>

      {/* Dialog Edit Plot */}
      <PlotEditDialog
        open={editDialogOpen}
        selectedOrder={selectedOrder}
        plotStatuses={plotStatuses}
        onClose={handleCloseEditDialog}
        onPlotStatusChange={handlePlotStatusChange}
        onSave={handleSavePlotStatuses}
        currentTab={currentTab}
        loading={loading}
      />
    </>
  );
};

export default BuahAlamiTableBody;